import { Component, Input, OnInit, OnChanges, SimpleChanges, Output, EventEmitter, Inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DealsPipeLineService } from 'src/app/services/deals-pipe-line.service';
import { AfterSalesSubStatusService } from 'src/app/services/after-sales-sub-status.service';
import { DealsEntityService } from 'src/app/services/deals-entity.service';
import { DealsStageService } from 'src/app/services/deals-stage.service';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { SubStatusUpdateDialogComponent } from './sub-status-update-dialog.component';

@Component({
  selector: 'app-after-sales-pipeline-tracker',
  templateUrl: './after-sales-pipeline-tracker.component.html',
  styleUrls: ['./after-sales-pipeline-tracker.component.scss']
})
export class AfterSalesPipelineTrackerComponent implements OnInit, OnChanges {

  /** Pass the full deal/lead object */
  @Input() entityObject: any;

  /** 'deal' | 'lead' — determines which API to call on sub-status update */
  @Input() entityType: 'deal' | 'lead' = 'deal';

  @Input() dealStages: any[] = [];

  /** Emits after a successful sub-status update so parent can reload */
  @Output() statusUpdated = new EventEmitter<void>();

  @Output() advanceDealStage = new EventEmitter<any>();

  stageList: any[] = [];
  subStatusList: any[] = [];
  historyList: any[] = [];
  showHistory: boolean = false;
  isUpdating: boolean = false;
  isInitializing: boolean = false;

  // Auto-detected from current deal stage's pipelineIds
  detectedPipelineIds: number[] = [];
  detectedPipelineName: string = '';
  isLoadingPipelineDetection: boolean = false;

  currentStageIndex: number = -1;
  currentSubStatusIndex: number = -1;

  get hasPipeline(): boolean {
    return !!(this.entityObject?.afterSalesPipelineId);
  }

  get currentStage(): any {
    return this.stageList[this.currentStageIndex] || null;
  }

  constructor(
    private dealsPipeLineService: DealsPipeLineService,
    private afterSalesSubStatusService: AfterSalesSubStatusService,
    private dealsEntityService: DealsEntityService,
    private dealsStageService: DealsStageService,
    private snackBar: MatSnackBar,
    public dialog: MatDialog
  ) { }

  ngOnInit(): void {
    this.loadData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['entityObject'] && !changes['entityObject'].firstChange) {
      this.loadData();
    }
  }

  loadData(): void {
    if (!this.entityObject?.entityId) return;

    // 1. ALWAYS fetch history first to get the absolute truth from MySQL, bypassing any Solr lag or missing schema fields.
    this.dealsEntityService.getAfterSalesHistory(this.entityObject.entityId).subscribe((res: any) => {
      this.historyList = res?.datalist || res || [];
      const latestHistory = this.historyList.length > 0 ? this.historyList[0] : null;

      if (latestHistory) {
        // Pipeline is definitively initialized in MySQL. Override whatever Solr gave us.
        this.entityObject.afterSalesPipelineId = latestHistory.pipelineId;
        this.entityObject.afterSalesStageId = latestHistory.stageId;
        this.entityObject.afterSalesSubStatusId = latestHistory.toSubStatusId;
      }

      if (!this.hasPipeline) {
        // If still no pipeline after checking history, it's truly uninitialized.
        this.detectAndLoadLocalPipeline();
        return;
      }

      this.detectedPipelineIds = [];
      this.detectedPipelineName = '';

      // 2. Load pipeline stages from DB saved state
      this.dealsPipeLineService.getDealsPipeLineStagesById(this.entityObject.afterSalesPipelineId).subscribe((stages: any[]) => {
        this.stageList = (stages || []).sort((a, b) => a.position - b.position);
        this.currentStageIndex = this.stageList.findIndex(
          s => s.pipelineStageId == this.entityObject.afterSalesStageId
        );
      });

      // 3. Load sub-statuses for current stage
      if (this.entityObject.afterSalesStageId) {
        this.afterSalesSubStatusService.getSubStatusesByPipelineStageId(this.entityObject.afterSalesStageId).subscribe((subs: any[]) => {
          this.subStatusList = subs || [];
          this.currentSubStatusIndex = this.subStatusList.findIndex(
            s => s.statusId == this.entityObject.afterSalesSubStatusId
          );
          if (this.currentSubStatusIndex === -1 && this.subStatusList.length > 0) {
            this.currentSubStatusIndex = 0;
            this.entityObject.afterSalesSubStatusId = this.subStatusList[0].statusId;
          }
        });
      }
    });
  }

  /**
   * If deal is not initialized, check current deal stage for pipelineIds.
   * If found, load the first pipeline locally so user can work on it immediately.
   */
  detectAndLoadLocalPipeline(): void {
    const statusId = this.entityObject?.statusId;
    if (!statusId) return;

    this.isLoadingPipelineDetection = true;
    const typeId = this.entityType === 'lead' ? 2 : 1;
    this.dealsStageService.getAllDealsStagesDropDownCache(typeId).subscribe({
      next: (stages: any[]) => {
        const currentStage = (stages || []).find(
          s => String(s.statusId) === String(statusId) || String(s.id) === String(statusId)
        );
        if (currentStage && currentStage.pipelineIds && currentStage.pipelineIds.length > 0) {
          this.detectedPipelineIds = currentStage.pipelineIds;
          const pipelineId = currentStage.pipelineIds[0];

          // Locally load the stages for this pipeline
          this.dealsPipeLineService.getDealsPipeLineStagesById(pipelineId).subscribe((stages: any[]) => {
            this.stageList = (stages || []).sort((a, b) => a.position - b.position);
            this.currentStageIndex = 0; // Default to first stage

            if (this.stageList.length > 0) {
              const firstStageId = this.stageList[0].pipelineStageId;
              this.afterSalesSubStatusService.getSubStatusesByPipelineStageId(firstStageId).subscribe((subs: any[]) => {
                this.subStatusList = subs || [];
                this.currentSubStatusIndex = this.subStatusList.length > 0 ? 0 : -1;
                this.isLoadingPipelineDetection = false;
                // The "No Pipeline" message will hide because we are faking the properties below
                this.entityObject.afterSalesPipelineId = pipelineId;
                this.entityObject.afterSalesStageId = firstStageId;
                this.entityObject.afterSalesStatus = this.stageList[0].statusName;
                if (this.subStatusList.length > 0) {
                  this.entityObject.afterSalesSubStatusId = this.subStatusList[0].statusId;
                }
              });
            } else {
              this.isLoadingPipelineDetection = false;
            }
          });
        } else {
          this.detectedPipelineIds = [];
          this.isLoadingPipelineDetection = false;
        }
      },
      error: () => { this.isLoadingPipelineDetection = false; }
    });
  }

  selectSubStatus(sub: any, index: number): void {
    if (this.isUpdating) return;

    // If it's a completely fresh local pipeline, we allow setting the first status
    if (this.currentSubStatusIndex !== -1 && sub.statusId === this.entityObject?.afterSalesSubStatusId) return;

    if (!this.entityObject?.entityId) return;

    const isFinalStage = index === this.subStatusList.length - 1;
    const requireNotes = sub.requireNotes === 1;
    const requireImages = sub.requireImages === 1;

    if (!requireNotes && !requireImages && !isFinalStage) {
      // Update silently if nothing is required and it's not the final stage
      this.performUpdate(sub, index, { notes: '', images: '' });
      return;
    }

    const dialogRef = this.dialog.open(SubStatusUpdateDialogComponent, {
      width: '500px',
      data: {
        nodeName: sub.statusName,
        isFinalStage: isFinalStage,
        requireNotes: requireNotes,
        requireImages: requireImages,
        dealStages: this.dealStages,
        currentDealStage: this.entityObject?.statusName || this.entityObject?.dealStage || this.entityObject?.stage,
        saveCallback: (payload: any) => this.performUpdate(sub, index, payload)
      }
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      // Result is true if saved successfully, nothing to do here as performUpdate already handled it
    });
  }

  private performUpdate(sub: any, index: number, payload: any): Promise<void> {
    return new Promise((resolve, reject) => {
      this.isUpdating = true;
      // Always include pipelineId and stageId so backend can use them for auto-advance
      const enrichedPayload = {
        ...payload,
        pipelineId: String(this.entityObject?.afterSalesPipelineId || ''),
        stageId: String(this.entityObject?.afterSalesStageId || '')
      };
      this.dealsEntityService.updateAfterSalesSubStatusWithImages(this.entityObject.entityId, sub.statusId, enrichedPayload).subscribe({
        next: () => {
          this.snackBar.open('After-Sales status updated!', 'A-', { panelClass: 'success', duration: 3000 });
          this.entityObject.afterSalesSubStatusId = sub.statusId;
          this.currentSubStatusIndex = index;
          this.isUpdating = false;
          if (this.showHistory) {
            this.loadHistory();
          }
          // Backend now handles auto-advance when moveToNextStage=1
          // Just emit statusUpdated so parent reloads the deal (which now returns the new stage from DB)
          this.statusUpdated.emit();
          resolve();
        },
        error: (err) => {
          this.snackBar.open(err?.error?.status_message || err?.error || 'Failed to update status', '×', { panelClass: 'error', duration: 4000 });
          this.isUpdating = false;
          reject(err);
        }
      });
    });
  }

  toggleHistory(): void {
    this.showHistory = !this.showHistory;
    if (this.showHistory && this.historyList.length === 0) {
      this.loadHistory();
    }
  }

  loadHistory(): void {
    this.dealsEntityService.getAfterSalesHistory(this.entityObject.entityId).subscribe((res: any) => {
      this.historyList = res?.datalist || res || [];
    });
  }


  getSubStatusClass(index: number): string {
    if (index < this.currentSubStatusIndex) return 'step-done';
    if (index === this.currentSubStatusIndex) return 'step-active';
    return 'step-pending';
  }

  getStageClass(index: number): string {
    if (index < this.currentStageIndex) return 'stage-done';
    if (index === this.currentStageIndex) return 'stage-active';
    return 'stage-pending';
  }

  /** Called when current stage has no sub-statuses — calls backend with a dummy sub-status to trigger auto-advance */
  advanceToNextStageManually(): void {
    if (this.isUpdating) return;
    if (!this.entityObject?.entityId) return;

    const nextStageIndex = this.currentStageIndex + 1;
    if (nextStageIndex >= this.stageList.length) return;

    const nextStage = this.stageList[nextStageIndex];
    this.isUpdating = true;

    // Build a direct request to advance the stage without going through sub-status
    // We use the advanceDealStage approach: directly update afterSalesStageId on the deal via backend
    const payload = {
      pipelineId: String(this.entityObject?.afterSalesPipelineId || ''),
      stageId: String(this.entityObject?.afterSalesStageId || ''),
      notes: this.subStatusList.length === 0 ? 'Manually advanced — no sub-tasks in this stage' : 'Manually advanced — all sub-tasks completed',
      images: ''
    };

    // Call backend advance-stage directly
    this.dealsEntityService.advanceAfterSalesStage(this.entityObject.entityId, nextStage.pipelineStageId, payload).subscribe({
      next: () => {
        this.isUpdating = false;
        this.snackBar.open(`Moved to ${nextStage.statusName}!`, 'A-', { panelClass: 'success', duration: 3000 });
        this.statusUpdated.emit();
      },
      error: (err) => {
        this.isUpdating = false;
        this.snackBar.open(err?.error?.status_message || 'Failed to advance stage', '×', { panelClass: 'error', duration: 4000 });
      }
    });
  }
}
